import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { AxiosError } from 'axios';
import ApiService from '../services/api';

function apiErrorMessage(err: unknown, fallback: string): string {
  const ax = err as AxiosError<{ detail?: unknown }>;
  const d = ax.response?.data?.detail;
  if (typeof d === 'string' && d.trim()) return d;
  if (Array.isArray(d)) {
    const parts = d.map((item) =>
      typeof item === 'object' && item !== null && 'msg' in item
        ? String((item as { msg: unknown }).msg)
        : JSON.stringify(item),
    );
    const joined = parts.join('; ').trim();
    if (joined) return joined;
  }
  if (d !== undefined && d !== null && typeof d === 'object') {
    try {
      return JSON.stringify(d);
    } catch {
      /* ignore */
    }
  }
  const status = ax.response?.status;
  if (ax.message) return status ? `${ax.message} (${status})` : ax.message;
  if (status) return `Request failed (${status})`;
  return fallback;
}

interface OrderingEmailConfirmationTabProps {
  onError?: (msg: string) => void;
}

const PLACEHOLDER_HELP = [
  '{{customer_name}}',
  '{{order_reference}}',
  '{{service_address}}',
  '{{billing_address}}',
  '{{requested_activation_date}}',
  '{{customer_email}}',
  '{{mobile_number}}',
  '{{date_of_birth}}',
  '{{customer_type}}',
  '{{organisation_name}}',
  '{{abn_acn}}',
  '{{payment_acknowledgment}}',
  '{{voice_summary}}',
  '{{order_lines_html}}',
  '{{order_totals_html}}',
].join(' · ');

const OrderingEmailConfirmationTab: React.FC<OrderingEmailConfirmationTabProps> = ({ onError }) => {
  const [layoutTemplateId, setLayoutTemplateId] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBodyHtml, setEmailBodyHtml] = useState('');
  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLocalError(null);
      const data = await ApiService.adminGetOrderConfirmationEmail();
      setLayoutTemplateId(data.layout_template_id ?? '');
      setEmailSubject(data.email_subject ?? '');
      setEmailBodyHtml(data.email_body_html ?? '');
      setOptions(data.layout_template_options ?? []);
    } catch (err: unknown) {
      const msg = apiErrorMessage(err, 'Failed to load order confirmation email settings');
      setLocalError(msg);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccess(null);
      setLocalError(null);
      const data = await ApiService.adminUpdateOrderConfirmationEmail({
        layout_template_id: layoutTemplateId,
        email_subject: emailSubject,
        email_body_html: emailBodyHtml,
      });
      setLayoutTemplateId(data.layout_template_id ?? '');
      setEmailSubject(data.email_subject ?? '');
      setEmailBodyHtml(data.email_body_html ?? '');
      setOptions(data.layout_template_options ?? []);
      setSuccess('Saved successfully.');
    } catch (err: unknown) {
      const msg = apiErrorMessage(err, 'Failed to save order confirmation email settings');
      setLocalError(msg);
      onError?.(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Order confirmation email
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        After a customer submits an order, we send this message via Mailgun. Payment card and bank details are
        never included—only a short acknowledgment of how they paid.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <strong>Layout (Case Module):</strong> choose a case email template whose HTML wraps the content below.
        Put <code>{'{{order_email_inner}}'}</code> in that template where the order body should appear (you can
        also use <code>%%email_body%%</code>, the same token as case emails). Leave empty to send only the inner
        HTML without a wrapper.
      </Typography>

      {localError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError(null)}>
          {localError}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TextField
        select
        fullWidth
        label="Layout template (optional)"
        value={layoutTemplateId}
        onChange={(e) => setLayoutTemplateId(e.target.value)}
        sx={{ mb: 2 }}
        helperText="From Settings → Case Module → Case Templates (e.g. Default)."
      >
        <MenuItem value="">
          <em>None — inner HTML only</em>
        </MenuItem>
        {options.map((o) => (
          <MenuItem key={o.id} value={o.id}>
            {o.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth
        label="Email subject"
        value={emailSubject}
        onChange={(e) => setEmailSubject(e.target.value)}
        sx={{ mb: 2 }}
        helperText="Supports the same {{placeholders}} as the body (e.g. {{order_reference}})."
      />

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" gutterBottom>
        Email content (inner HTML)
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {PLACEHOLDER_HELP}
      </Typography>

      <TextField
        fullWidth
        multiline
        minRows={16}
        label="HTML body"
        value={emailBodyHtml}
        onChange={(e) => setEmailBodyHtml(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{ sx: { fontFamily: 'ui-monospace, monospace', fontSize: '0.8125rem', alignItems: 'flex-start' } }}
      />

      <Button variant="contained" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save'}
      </Button>
    </Paper>
  );
};

export default OrderingEmailConfirmationTab;
